import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Edit,
  Users,
  Calendar,
  MapPin,
  Briefcase,
  DollarSign,
  Settings,
} from 'lucide-react'
import { jobsApi } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PipelineStageManager } from '@/components/features/jobs/PipelineStageManager'
import { CustomQuestionsManager } from '@/components/features/jobs/CustomQuestionsManager'
import { JobActions } from '@/components/features/jobs/JobActions'
import { KanbanBoard } from '@/components/features/pipeline/KanbanBoard'
import { formatDate, formatSalary, getInitials } from '@/lib/utils'

const statusColors: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  published: 'bg-success/10 text-success',
  paused: 'bg-warning/10 text-warning',
  closed: 'bg-muted text-muted-foreground',
}

export function JobDetailPage() {
  const { jobId } = useParams({ from: '/_app/jobs/$jobId' })
  const [activeTab, setActiveTab] = useState('overview')
  const queryClient = useQueryClient()

  const { data: job, isLoading } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => jobsApi.get(jobId),
  })

  const { data: pipeline } = useQuery({
    queryKey: ['job-pipeline', jobId],
    queryFn: () => jobsApi.getPipeline(jobId),
    enabled: !!job,
  })

  const { data: questions } = useQuery({
    queryKey: ['job-questions', jobId],
    queryFn: () => jobsApi.getQuestions(jobId),
    enabled: !!job,
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    )
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Job not found</h2>
        <Button variant="link" asChild className="mt-4">
          <Link to="/jobs">Back to jobs</Link>
        </Button>
      </div>
    )
  }

  const jobData = job as any

  return (
    <PageLayout fullHeight>
      <div className="h-full flex flex-col overflow-y-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/jobs">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{jobData.title}</h1>
              <Badge className={statusColors[jobData.status] || statusColors.draft}>
                {jobData.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              {jobData.department && (
                <span className="flex items-center gap-1">
                  <Briefcase className="h-3.5 w-3.5" />
                  {jobData.department.name}
                </span>
              )}
              {jobData.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {jobData.location.name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                Posted {formatDate(jobData.createdAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/jobs/$jobId/edit" params={{ jobId }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </Button>
          <JobActions
            jobId={jobId}
            status={jobData.status || 'draft'}
            onStatusChange={() => {
              queryClient.invalidateQueries({ queryKey: ['job', jobId] })
            }}
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pipeline">
            <Users className="h-4 w-4 mr-1" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-1" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{jobData.applicationCount || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Applicants</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-success/10">
                    <Calendar className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {(pipeline as any[])?.find((s: any) => s.type === 'interview')?.applicationCount || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">In Interview</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-warning/10">
                    <DollarSign className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {(pipeline as any[])?.find((s: any) => s.type === 'offer')?.applicationCount || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Offers Extended</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {(pipeline as any[])?.find((s: any) => s.type === 'hired')?.applicationCount || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Hired</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pipeline */}
          <Card>
            <CardHeader>
              <CardTitle>Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {(pipeline as any[] || []).map((stage: any) => (
                  <motion.div
                    key={stage.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex-shrink-0 w-48"
                  >
                    <div
                      className="p-3 rounded-lg border-2"
                      style={{ borderColor: stage.color + '40', backgroundColor: stage.color + '10' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className="text-sm font-medium"
                          style={{ color: stage.color }}
                        >
                          {stage.name}
                        </span>
                        <span className="text-lg font-bold">{stage.applicationCount || 0}</span>
                      </div>
                      <div
                        className="h-1.5 rounded-full"
                        style={{ backgroundColor: stage.color + '30' }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            backgroundColor: stage.color,
                            width: `${Math.min((stage.applicationCount / (jobData.applicationCount || 1)) * 100, 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Job Details */}
          <div className="grid gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: jobData.description }}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Job Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Employment Type</p>
                    <p className="font-medium capitalize">{jobData.employmentType?.replace('-', ' ')}</p>
                  </div>
                  {jobData.experienceLevel && (
                    <div>
                      <p className="text-sm text-muted-foreground">Experience Level</p>
                      <p className="font-medium capitalize">{jobData.experienceLevel}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Remote Policy</p>
                    <p className="font-medium capitalize">{jobData.remotePolicy}</p>
                  </div>
                  {(jobData.salaryMin || jobData.salaryMax) && (
                    <div>
                      <p className="text-sm text-muted-foreground">Salary Range</p>
                      <p className="font-medium">
                        {formatSalary(
                          jobData.salaryMin,
                          jobData.salaryMax,
                          jobData.salaryCurrency,
                          jobData.salaryPeriod
                        )}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {jobData.hiringManager && (
                <Card>
                  <CardHeader>
                    <CardTitle>Hiring Manager</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={jobData.hiringManager.avatarUrl} />
                        <AvatarFallback>
                          {getInitials(jobData.hiringManager.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{jobData.hiringManager.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {jobData.hiringManager.email}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-6">
          <KanbanBoard jobId={jobId} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <PipelineStageManager
            jobId={jobId}
            stages={(pipeline as any[]) || []}
            onUpdate={() => queryClient.invalidateQueries({ queryKey: ['job-pipeline', jobId] })}
          />

          <CustomQuestionsManager
            jobId={jobId}
            questions={(questions as any[]) || []}
            onUpdate={() => queryClient.invalidateQueries({ queryKey: ['job-questions', jobId] })}
          />
        </TabsContent>
      </Tabs>
      </div>
    </PageLayout>
  )
}
